import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
    IForgotPasswordPayload,
    IForgotPasswordResponse,
    ILoginPayload,
    ILoginResponse,
    ILoginVerifyPayload,
    ISetPasswordPayload,
    ISetPasswordResponse,
    ISignupAccepted,
    ISignupPayload,
    ISignupVerified,
    ISignupVerifyPayload,
    ISwitchableTenants,
} from '../interface/auth.interface';
import { IResponse } from '../../shared/interface/api.interface';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';

@Injectable()
export class AuthService {
    private loginUrl = '/auth/login';
    private signupUrl = '/auth/signup';
    private ForgotPasswordUrl = '/auth/forgot-password';
    private setPasswordUrl = '/auth/reset-password';
    private googleAdminLoginUrl = '/auth/social/google/admin/login';
    private googleSignupUrl = '/auth/social/google/signup/login';
    private logoutUrl = '/auth/logout';
    private switchTenantUrl = '/auth/switch-tenant';
    private switchableTenantsUrl = '/auth/switchable-tenants';

    constructor(private http: HttpClient) {}

    /*=====================================
      Builds the URL for a plain browser navigation to start the admin Google login flow. This
      is not an HttpClient call, so the HTTP interceptor won't prefix it — the base path is
      built manually here. The backend always redirects back to its own configured admin portal
      URL, so no query params are needed.
      @return the absolute URL to navigate the browser to
    ======================================*/
    getGoogleLoginUrl(): string {
        return `${environment.apiHost}${environment.basePath}${this.googleAdminLoginUrl}`;
    }

    async login(loginPayload: ILoginPayload): Promise<ILoginResponse> {
        return firstValueFrom(
            this.http.post<IResponse<ILoginResponse>>(this.loginUrl, loginPayload),
        ).then((res) => res.data);
    }

    async verifyLoginOtp(payload: ILoginVerifyPayload): Promise<ILoginResponse> {
        return firstValueFrom(
            this.http.post<IResponse<ILoginResponse>>(`${this.loginUrl}/verify-otp`, payload),
        ).then((res) => res.data);
    }

    /*=====================================
      Builds the URL that starts merchant sign-up with Google. This is a plain
      browser navigation, not an HttpClient call, so the interceptor does not
      prefix it and the base path is built here - the same shape the sign-in
      screen's Google button already uses.
      @return the absolute URL to navigate to
    ======================================*/
    getGoogleSignupUrl(): string {
        return `${environment.apiHost}${environment.basePath}${this.googleSignupUrl}`;
    }

    async signup(payload: ISignupPayload): Promise<ISignupAccepted> {
        return firstValueFrom(
            this.http.post<IResponse<ISignupAccepted>>(`${this.signupUrl}/account`, payload),
        ).then((res) => res.data);
    }

    async resendSignupCode(email: string): Promise<ISignupAccepted> {
        return firstValueFrom(
            this.http.post<IResponse<ISignupAccepted>>(`${this.signupUrl}/resend`, { email }),
        ).then((res) => res.data);
    }

    async verifySignupOtp(payload: ISignupVerifyPayload): Promise<ISignupVerified> {
        return firstValueFrom(
            this.http.post<IResponse<ISignupVerified>>(`${this.signupUrl}/verify`, payload),
        ).then((res) => res.data);
    }

    async forgotPassword(
        forgotPasswordPayload: IForgotPasswordPayload,
    ): Promise<IForgotPasswordResponse> {
        return firstValueFrom(
            this.http.post<IResponse<IForgotPasswordResponse>>(
                this.ForgotPasswordUrl,
                forgotPasswordPayload,
            ),
        ).then((res) => res.data);
    }

    async setPassword(setPasswordPayload: ISetPasswordPayload): Promise<ISetPasswordResponse> {
        return firstValueFrom(
            this.http.post<IResponse<ISetPasswordResponse>>(
                this.setPasswordUrl,
                setPasswordPayload,
            ),
        ).then((res) => res.data);
    }

    async logout(): Promise<void> {
        await firstValueFrom(this.http.post<IResponse<{ success: boolean }>>(this.logoutUrl, {}));
    }

    /*=====================================
      Switches the session to a different tenant. Pass a known membershipId,
      or a tenantId when none is known yet (a TT_USER account is checked into
      that store on the backend); both omitted/null switches to Seldit.
      Returns a fresh token — callers must persist it (and re-fetch
      /users/me) before anything else.
      @param target - the membership or tenant to switch into
      @return the fresh token
    ======================================*/
    /*=====================================
      The tenants this account may switch into, with the two numbers behind the
      switcher's "N of 5 used" line.

      Lists MEMBERSHIPS, not ownership: somebody who is staff in a shop they do
      not own belongs in this list. Ownership is only ever the ceiling's count.
      @return the tenants, how many are owned, and the ceiling
    ======================================*/
    async getSwitchableTenants(): Promise<ISwitchableTenants> {
        return firstValueFrom(
            this.http.get<IResponse<ISwitchableTenants>>(this.switchableTenantsUrl),
        ).then((res) => res.data);
    }

    async switchTenant(target: {
        membershipId?: string | null;
        tenantId?: string | null;
    }): Promise<ILoginResponse> {
        return firstValueFrom(
            this.http.post<IResponse<ILoginResponse>>(this.switchTenantUrl, target),
        ).then((res) => res.data);
    }
}
